import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  MessageSquare, 
  Handshake, 
  Truck, 
  ShieldCheck, 
  MapPin, 
  CheckCircle, 
  AlertCircle,
  ArrowRight,
  Search,
  CreditCard,
  PackageCheck,
  Leaf,
  HelpCircle,
  ShoppingCart
} from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer'; // Import Footer
import { useAuth } from '../context/AuthContext';
import MobileBottomNav from '../components/layout/MobileBottomNav';

const HowToBuy = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [activeStep, setActiveStep] = useState(null);

  const brandColor = '#ff660e';
  const brandColorLight = '#fff0e6';

  const buyingSteps = [
    {
      id: 1,
      title: "Browse & Search",
      description: "Find products or services you need using our search and category filters",
      icon: <Search className="w-6 h-6" />,
      details: [
        "Use the search bar to find specific items",
        "Browse through categories to discover products",
        "Filter results by price, location, and condition",
        "Save items you're interested in to favorites"
      ],
      tips: ["Be specific with your search terms", "Check seller ratings before contacting"],
      iconColor: "text-blue-500",
      bgColor: "bg-blue-50"
    },
    {
      id: 2,
      title: "Start Conversation",
      description: "Message the seller directly to ask questions and express interest",
      icon: <MessageSquare className="w-6 h-6" />,
      details: [
        "Click 'Message Seller' on the product page",
        "Introduce yourself politely",
        "Ask specific questions about the item",
        "Request more photos if needed"
      ],
      tips: ["Be clear about your interest", "Respond promptly to messages"],
      iconColor: "text-green-500",
      bgColor: "bg-green-50"
    },
    {
      id: 3,
      title: "Negotiate & Agree",
      description: "Discuss and agree on the price, terms, and conditions with the seller",
      icon: <Handshake className="w-6 h-6" />,
      details: [
        "Make a reasonable offer if price is negotiable",
        "Agree on payment terms",
        "Discuss delivery or pickup options",
        "Confirm all details before proceeding"
      ],
      tips: ["Be respectful in negotiations", "Get all agreements in writing"],
      iconColor: "text-yellow-500",
      bgColor: "bg-yellow-50"
    },
    {
      id: 4,
      title: "Arrange Delivery/Pickup",
      description: "Plan how you'll receive the item - delivery or in-person pickup",
      icon: <Truck className="w-6 h-6" />,
      details: [
        "Discuss delivery costs and timelines",
        "For pickup, arrange a safe public meeting location",
        "Share only necessary contact information",
        "Agree on packaging requirements"
      ],
      tips: ["Meet in daylight hours", "Bring a friend for in-person meetings"],
      iconColor: "text-purple-500",
      bgColor: "bg-purple-50"
    },
    {
      id: 5,
      title: "Secure Payment",
      description: "Use our escrow system for safe transactions - never pay directly to seller",
      icon: <ShieldCheck className="w-6 h-6" />,
      details: [
        "If seller requests upfront payment, use our escrow",
        "Funds are held securely until you confirm receipt",
        "Partial payments for delivery costs are protected",
        "No direct bank transfers to seller accounts"
      ],
      warning: "⚠️ Never send money directly to seller's personal accounts",
      iconColor: "text-red-500",
      bgColor: "bg-red-50"
    },
    {
      id: 6,
      title: "Meet & Inspect",
      description: "Meet in public location and thoroughly inspect the product",
      icon: <MapPin className="w-6 h-6" />,
      details: [
        "Choose well-lit, public locations",
        "Bring a friend or family member",
        "Test electronics and check all functions",
        "Verify authenticity of branded items"
      ],
      warning: "Your safety is our priority - never meet in private locations",
      iconColor: "text-indigo-500",
      bgColor: "bg-indigo-50"
    },
    {
      id: 7,
      title: "Confirm Satisfaction",
      description: "Ensure you're completely satisfied with the product before releasing funds",
      icon: <CheckCircle className="w-6 h-6" />,
      details: [
        "Take your time to inspect thoroughly",
        "Test all features and functions",
        "Check for any hidden damages",
        "Verify item matches the description"
      ],
      tips: ["Don't feel pressured to release funds", "Contact support if there are issues"],
      iconColor: "text-emerald-500",
      bgColor: "bg-emerald-50"
    },
    {
      id: 8,
      title: "Release Funds",
      description: "Once satisfied, release payment to the seller through the platform",
      icon: <CreditCard className="w-6 h-6" />,
      details: [
        "Click 'Confirm Receipt' in your orders",
        "Funds are immediately released to seller",
        "Transaction is now complete",
        "Leave feedback for the seller"
      ],
      warning: "⚠️ Released funds cannot be reversed - ensure satisfaction first",
      iconColor: "text-orange-500",
      bgColor: "bg-orange-50"
    }
  ];

  const safetyTips = [
    {
      title: "Verify Seller",
      content: "Check seller ratings, reviews, and verification status before transacting"
    },
    {
      title: "Use Escrow",
      content: "Always use our escrow service for any upfront or partial payments"
    },
    {
      title: "Public Meetings",
      content: "Meet in well-lit, public places with surveillance cameras"
    },
    {
      title: "Inspect Thoroughly",
      content: "Don't release funds until you've completely inspected the item"
    },
    {
      title: "Report Issues",
      content: "Contact support immediately if you encounter any problems or scams"
    }
  ];

  const handleStepClick = (stepId) => {
    setActiveStep(activeStep === stepId ? null : stepId);
  };

  const handleRaiseDispute = () => {
    if (user) {
      navigate('/chats');
    } else {
      navigate('/login', { state: { redirectTo: '/dispute/new' } });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header with EcoMart Branding */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl mb-6"
               style={{ backgroundColor: brandColorLight }}>
            <div className="flex items-center">
              <ShoppingCart className="w-8 h-8 ml-1" style={{ color: brandColor }} />
            </div>
          </div>
          <div className="flex items-center justify-center mb-3">
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
              How to Buy Safely 
            </h1>
            
          </div>
          <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto px-4">
            Follow these steps to ensure a safe and secure shopping experience on our sustainable marketplace
          </p>
        </div>

        {/* Safety Alert with Brand Color */}
        <div className="rounded-2xl p-6 mb-10 border" 
             style={{ 
               backgroundColor: brandColorLight,
               borderColor: brandColor + '30'
             }}>
          <div className="flex flex-col sm:flex-row items-start sm:items-center">
            <AlertCircle className="w-6 h-6 mr-3 mb-3 sm:mb-0 flex-shrink-0" style={{ color: brandColor }} />
            <div className="flex-1">
              <h3 className="text-lg font-semibold mb-2" style={{ color: brandColor }}>
                Important Safety Notice
              </h3>
              <p className="text-gray-800 mb-3">
                To protect yourself from scams, <strong>never pay sellers directly</strong> outside our platform. 
                Always use our secure escrow service for any payments before receiving your item.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => navigate('/support')}
                  className="inline-flex items-center font-medium hover:underline"
                  style={{ color: brandColor }}
                >
                  Report suspicious activity
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>
                <button
                  onClick={handleRaiseDispute}
                  className="inline-flex items-center font-medium hover:underline"
                  style={{ color: brandColor }}
                >
                  Raise escrow dispute
                  <ArrowRight className="w-4 h-4 ml-1" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Steps List */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Buying Process Steps</h2>
              
              <div className="space-y-3 md:space-y-4">
                {buyingSteps.map((step) => (
                  <div
                    key={step.id}
                    className={`border rounded-xl transition-all hover:shadow-md cursor-pointer overflow-hidden ${
                      activeStep === step.id ? 'border-orange-300' : 'border-gray-200'
                    }`}
                    style={{ 
                      borderLeftColor: activeStep === step.id ? brandColor : '',
                      borderLeftWidth: activeStep === step.id ? '4px' : ''
                    }}
                    onClick={() => handleStepClick(step.id)}
                  >
                    <div className="p-4 md:p-5">
                      <div className="flex items-start">
                        <div className={`p-3 rounded-lg ${step.bgColor} mr-3 md:mr-4 flex-shrink-0`}>
                          <div className={step.iconColor}>{step.icon}</div>
                        </div>
                        
                        <div className="flex-1 min-w-0"> {/* Added min-w-0 for text truncation */}
                          <div className="flex items-start justify-between">
                            <div className="min-w-0"> {/* Added for text truncation */}
                              <div className="flex items-center mb-2 flex-wrap">
                                <span className="inline-flex items-center justify-center w-7 h-7 md:w-8 md:h-8 text-white rounded-full text-sm font-bold mr-2 md:mr-3 flex-shrink-0"
                                      style={{ backgroundColor: brandColor }}>
                                  {step.id}
                                </span>
                                <h3 className="text-base md:text-lg font-semibold text-gray-900 truncate">
                                  {step.title}
                                </h3>
                              </div>
                              <p className="text-sm md:text-base text-gray-600 mb-2 md:mb-3 line-clamp-2">
                                {step.description}
                              </p>
                            </div>
                            
                            <button className="text-gray-400 hover:text-orange-500 ml-2 md:ml-4 flex-shrink-0">
                              <ArrowRight className={`w-4 h-4 md:w-5 md:h-5 transition-transform ${
                                activeStep === step.id ? 'rotate-90' : ''
                              }`} />
                            </button>
                          </div>

                          {/* Expanded Details */}
                          {activeStep === step.id && (
                            <div className="mt-3 md:mt-4 pt-3 md:pt-4 border-t border-gray-200">
                              <h4 className="font-medium text-gray-900 mb-2 md:mb-3">Detailed Instructions:</h4>
                              <ul className="space-y-1 md:space-y-2 mb-3 md:mb-4">
                                {step.details.map((detail, index) => (
                                  <li key={index} className="flex items-start">
                                    <CheckCircle className="w-4 h-4 md:w-5 md:h-5 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                                    <span className="text-sm md:text-base text-gray-700">{detail}</span>
                                  </li>
                                ))}
                              </ul>
                              
                              {step.tips && (
                                <div className="bg-blue-50 rounded-lg p-3 md:p-4 mb-3 md:mb-4">
                                  <h5 className="font-medium text-blue-800 mb-1 md:mb-2 text-sm md:text-base">
                                    💡 Pro Tips:
                                  </h5>
                                  <ul className="text-blue-700 space-y-1 text-sm md:text-base">
                                    {step.tips.map((tip, index) => (
                                      <li key={index}>{tip}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              
                              {step.warning && (
                                <div className="bg-red-50 border border-red-100 rounded-lg p-3 md:p-4">
                                  <h5 className="font-medium text-red-800 mb-1 text-sm md:text-base">
                                    ⚠️ Important:
                                  </h5>
                                  <p className="text-red-700 text-sm md:text-base">{step.warning}</p>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* FAQ Section */}
            <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Frequently Asked Questions</h2>
              
              <div className="space-y-3 md:space-y-4">
                {[
                  {
                    q: "What happens if I receive a damaged item?",
                    a: "Contact support immediately before releasing funds. We'll help mediate the situation and may freeze the transaction until resolved."
                  },
                  {
                    q: "Can I cancel a payment after releasing funds?",
                    a: "No, once funds are released to the seller, the transaction is complete and cannot be reversed. Always verify satisfaction first."
                  },
                  {
                    q: "How long does escrow hold funds?",
                    a: "Funds are held until you confirm receipt. If you don't confirm within 48 hours of delivery, the seller can request a release review."
                  },
                  {
                    q: "What if the seller doesn't deliver?",
                    a: "Report to support immediately. We'll investigate and refund your escrow payment if the seller fails to deliver as agreed."
                  },
                  {
                    q: "How do I raise a dispute for escrow issues?",
                    a: "Click 'Raise Dispute' in your order details or contact our support team. We'll investigate and resolve the issue within 48 hours."
                  }
                ].map((faq, index) => (
                  <div key={index} className="border border-gray-200 rounded-xl p-4 md:p-5">
                    <h3 className="font-semibold text-gray-900 mb-2 text-base md:text-lg">{faq.q}</h3>
                    <p className="text-gray-600 text-sm md:text-base">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - Safety Tips & Quick Links */}
          <div className="space-y-6">
            {/* Safety Tips */}
            <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6">
              <div className="flex items-center mb-4">
                <ShieldCheck className="w-5 h-5 mr-2" style={{ color: brandColor }} />
                <h2 className="text-xl font-bold text-gray-900">Safety Checklist</h2>
              </div>
              
              <div className="space-y-3 md:space-y-4">
                {safetyTips.map((tip, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold mr-3 mt-0.5 text-white"
                         style={{ backgroundColor: brandColor }}>
                      {index + 1}
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1 text-sm md:text-base">{tip.title}</h4>
                      <p className="text-sm text-gray-600">{tip.content}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Links */}
            <div className="rounded-2xl p-4 md:p-6 border"
                 style={{ 
                   backgroundColor: brandColorLight,
                   borderColor: brandColor + '30'
                 }}>
              <h2 className="text-xl font-bold text-gray-900 mb-4">Need Help?</h2>
              
              <div className="space-y-3">
                <button
                  onClick={() => navigate('/support')}
                  className="w-full flex items-center justify-between p-3 md:p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <MessageSquare className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: brandColor }} />
                    <span className="font-medium text-gray-900 truncate">Contact Support</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-600 flex-shrink-0" />
                </button>
                
                <button
                  onClick={handleRaiseDispute}
                  className="w-full flex items-center justify-between p-3 md:p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <AlertCircle className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: brandColor }} />
                    <span className="font-medium text-gray-900 truncate">Raise a Dispute</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-600 flex-shrink-0" />
                </button>
                
                <button
                  onClick={() => navigate('/escrow')}
                  className="w-full flex items-center justify-between p-3 md:p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <PackageCheck className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: brandColor }} />
                    <span className="font-medium text-gray-900 truncate">How Escrow Works</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-600 flex-shrink-0" />
                </button>
                
                <button
                  onClick={() => navigate('/support')}
                  className="w-full flex items-center justify-between p-3 md:p-4 bg-white rounded-xl hover:bg-gray-50 transition-colors group border border-gray-200"
                >
                  <div className="flex items-center min-w-0">
                    <HelpCircle className="w-5 h-5 mr-3 flex-shrink-0" style={{ color: brandColor }} />
                    <span className="font-medium text-gray-900 truncate">Help Center</span>
                  </div>
                  <ArrowRight className="w-4 h-4 text-gray-400 group-hover:text-orange-600 flex-shrink-0" />
                </button>
              </div>
            </div>

            {/* Start Shopping CTA */}
            <div className="rounded-2xl p-4 md:p-6 text-white overflow-hidden relative"
                 style={{ backgroundColor: brandColor }}>
              <div className="absolute top-0 right-0 w-24 h-24 opacity-10">
                <ShoppingBag className="w-24 h-24" />
              </div>
              
              <div className="relative z-10">
                <div className="flex items-center mb-4">
                  <ShoppingBag className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-bold mb-3">Ready to Shop?</h3>
                <p className="mb-6 opacity-95">
                  Apply what you've learned and start shopping sustainably on EcoMart
                </p>
                <button
                  onClick={() => navigate('/categories')}
                  className="w-full bg-white font-semibold py-3 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center"
                  style={{ color: brandColor }}
                >
                  Browse EcoMart Products
                  <ArrowRight className="w-4 h-4 ml-2" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add Footer */}
      <Footer />

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
    </div>
  );
};

export default HowToBuy;